import React, { createContext, useContext, useState, forwardRef } from 'react';

const SelectContext = createContext({
    value: undefined,
    onValueChange: () => { },
    open: false,
    setOpen: () => { }
});

const triggerStyle = {
    display: 'flex',
    height: '2.5rem',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-strong)',
    backgroundColor: 'var(--surface-raised)',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: 'var(--text)',
    fontFamily: 'var(--font-ui)',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box'
};

const contentStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 50,
    minWidth: '8rem',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--surface-raised)',
    boxShadow: 'var(--shadow-md)',
    marginTop: '0.25rem',
    boxSizing: 'border-box'
};

const itemStyle = {
    position: 'relative',
    display: 'flex',
    width: '100%',
    cursor: 'pointer',
    userSelect: 'none',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    fontFamily: 'var(--font-ui)',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'var(--text)',
    boxSizing: 'border-box',
    transition: 'background-color 0.15s ease'
};

export const Select = forwardRef(({ children, value, onValueChange, className, style, ...props }, ref) => {
    const [open, setOpen] = useState(false);
    return (
        <SelectContext.Provider value={{ value, onValueChange, open, setOpen }}>
            <div
                ref={ref}
                className={className}
                style={{ position: 'relative', width: '100%', ...style }}
                {...props}
            >
                {children}
            </div>
        </SelectContext.Provider>
    );
});
Select.displayName = 'Select';

export const SelectTrigger = forwardRef(({ className, children, style, ...props }, ref) => {
    const { setOpen, open } = useContext(SelectContext);
    return (
        <button
            ref={ref}
            type="button"
            onClick={() => setOpen(!open)}
            className={className}
            style={{ ...triggerStyle, ...style }}
            {...props}
        >
            {children}
            <span style={{ opacity: 0.6, fontSize: '0.75rem', marginLeft: '0.5rem', color: 'var(--text-muted)' }}>
                ▼
            </span>
        </button>
    );
});
SelectTrigger.displayName = 'SelectTrigger';

export const SelectValue = ({ placeholder, children }) => {
    const { value } = useContext(SelectContext);
    return (
        <span style={{ pointerEvents: 'none', color: (children || value) ? 'var(--text)' : 'var(--text-muted)' }}>
            {children || value || placeholder}
        </span>
    );
};

export const SelectContent = forwardRef(({ className, children, style, ...props }, ref) => {
    const { open } = useContext(SelectContext);
    if (!open) return null;

    return (
        <div
            ref={ref}
            className={className}
            style={{ ...contentStyle, ...style }}
            {...props}
        >
            <div style={{ padding: '0.25rem' }}>{children}</div>
        </div>
    );
});
SelectContent.displayName = 'SelectContent';

export const SelectItem = forwardRef(({ className, children, value, style, ...props }, ref) => {
    const { onValueChange, setOpen, value: selectedValue } = useContext(SelectContext);

    const handleSelect = () => {
        onValueChange?.(value);
        setOpen(false);
    };

    const isSelected = selectedValue === value;

    return (
        <div
            ref={ref}
            role="option"
            aria-selected={isSelected}
            onClick={handleSelect}
            className={className}
            style={{
                ...itemStyle,
                backgroundColor: isSelected ? 'var(--surface-sunken)' : 'transparent',
                fontWeight: isSelected ? '600' : '400',
                ...style
            }}
            {...props}
        >
            {isSelected && (
                <span style={{ marginRight: '0.5rem', color: 'var(--accent-ink)' }}>
                    ✓
                </span>
            )}
            <span style={{ flexGrow: 1 }}>{children}</span>
        </div>
    );
});
SelectItem.displayName = 'SelectItem';

export default Select;
